import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr";
import { countryCodes } from "apps/visits/packages/shared/static/country-codes";
import { Form } from "apps/visits/packages/ui/src/components";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/shared/v2/lib/utils";
import { Button } from "@/core/shared/v2/ui/button";
import { Input } from "@/core/shared/v2/ui/input";
import { Label } from "@/core/shared/v2/ui/label";
import { Select } from "@/core/shared/v2/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/core/shared/v2/ui/tooltip";

interface PhoneInputStepProps {
  dialCode: string;
  phone: string;
  onDialCodeChange: (dialCode: string) => void;
  onPhoneChange: (phone: string) => void;
  onSubmit: (dialCode: string, phone: string) => void;
  onChangeTrigger: () => void;
  isLoading?: boolean;
  isLocked?: boolean;
  error?: string | null;
}

export const PhoneInputStep = ({
  dialCode,
  phone,
  onDialCodeChange,
  onPhoneChange,
  onSubmit,
  onChangeTrigger,
  isLoading,
  isLocked,
  error,
}: PhoneInputStepProps) => {
  const { t } = useTranslation();
  const knownDialCodes = useMemo(() => new Set(countryCodes.map((c) => c.dial_code.replace(/ /g, ""))), []);
  // Native <select value=X> where X isn't an option silently falls back to the first option
  // (+93 Afghanistan). Sanitize before passing anywhere user-visible.
  const sanitizeDialCode = useCallback(
    (value: string | undefined) => (value && knownDialCodes.has(value) ? value : "+48"),
    [knownDialCodes],
  );

  const safeDialCode = sanitizeDialCode(dialCode);

  const handleSubmit = () => {
    const trimmed = phone.trim();
    if (trimmed) {
      onSubmit(safeDialCode, trimmed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit();
    }
  };

  // Locked state — compact card replaces the entire form. Once the user has
  // confirmed their number, the inputs and submit CTA are no longer relevant;
  // showing the bulky disabled form just eats screen real estate. The pencil
  // affordance reuses the existing `onChangeTrigger` callback (formerly the
  // "Zmień" button), so flow-wise nothing else changes.
  if (isLocked) {
    return (
      <div className="flex items-end justify-between gap-3 border-b bg-white p-2">
        <span className="flex gap-2 font-medium text-base text-foreground max-lg:-mt-2 max-lg:flex-col">
          <span className="">Zatwierdzony numer:</span>{" "}
          <span className="opacity-75">
            {safeDialCode} {phone}
          </span>
        </span>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onChangeTrigger}
                aria-label="Zmień numer"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-gray-100 hover:text-dodger-blue-700"
              >
                <PencilSimpleIcon size={20} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">Zmień numer</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return (
    <div className="mb-2 flex flex-col gap-3">
      {/* Wrapper around the country+phone row and the submit button. `md:relative`
          scopes the button's absolute positioning to this block alone, so the
          (potentially present) error message rendered below doesn't shift where
          the button lands on desktop. */}
      <div className="flex flex-col gap-3 md:relative">
        {/* Country + phone always sit in a row (mobile too). On desktop the
            submit button overlays the right side of the phone input; on mobile
            it drops to a full-width block below — implemented as a single
            Button with responsive positioning. */}
        <div className="flex flex-row items-end gap-3">
          <Form.Control className="w-28 shrink-0 md:w-36">
            <div className="flex flex-col gap-2">
              <Label htmlFor="authDialCode">{t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.COUNTRY")} *</Label>
              {/* React 18 bug: updating a controlled <select> with a large dynamic
                  option list can silently fail — React commits the prop without
                  writing selectedIndex to the DOM, falling back to index 0
                  (+93 Afghanistan). Using `defaultValue` + `key={safeDialCode}`
                  makes the select uncontrolled: React never needs to update the
                  value in-place. A remount only happens when the user explicitly
                  picks a different country (safeDialCode changes), which is the
                  only moment a fresh DOM node is actually needed. */}
              <Select
                key={safeDialCode}
                defaultValue={safeDialCode}
                onChange={(e) => onDialCodeChange(e.target.value)}
                name="authDialCode"
                disabled={isLoading}
              >
                {countryCodes.map((country) => (
                  <Select.Option value={country.dial_code.replace(/ /g, "")} key={country.code}>
                    ({country.dial_code})
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Form.Control>
          <div className="w-full">
            <div className="flex flex-col gap-2">
              <Label htmlFor="authPhone">{t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.PHONE")} *</Label>
              <Input
                name="authPhone"
                value={phone}
                onChange={(e) => onPhoneChange(e.target.value)}
                onKeyDown={handleKeyDown}
                required
                inputMode="numeric"
                disabled={isLoading}
                className="w-full md:pr-[140px]"
                wrap={{ className: "w-full" }}
                aria-invalid={!!error}
              />
            </div>
          </div>
        </div>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading || !phone.trim()}
          variant={isLoading || !phone.trim() ? "tertiary" : "primary"}
          className={cn(
            // Mobile: static block, full-width below the country+phone row.
            // Desktop: absolute over the right edge of the phone input — the
            // wrapping div is md:relative and the phone input reserves md:pr-[140px].
            "h-10 w-full rounded-md border-none px-4",
            "md:absolute md:right-1 md:bottom-1 md:z-10 md:w-auto md:min-w-36",
            (isLoading || !phone.trim()) && "disabled:opacity-60",
            !isLoading && phone.trim() && "bg-dodger-blue-700 hover:bg-dodger-blue-700",
          )}
        >
          Zatwierdź numer
        </Button>
      </div>
      {error && <p className="font-medium text-destructive text-sm">{error}</p>}
    </div>
  );
};
